import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Copy, Heart } from "lucide-react";
import { FanChoiceIcon } from "../components/FanChoiceIcon";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Countdown } from "@/components/fanvote/FanVoteParts";

const VoteBox = ({ d, entryId, reload }) => {
  const opened = useRef(Date.now());
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const next = `/fan-vote/entry/${entryId}`;
  const vote = async () => {
    setBusy(true);
    try { await api.post(`/fan-vote/entries/${entryId}/vote`, { website: hp, elapsed_ms: Date.now() - opened.current }); toast.success("Vote counted. Thank you!"); await reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Vote failed"); }
    setBusy(false);
  };
  const sendVerify = async () => {
    try { await api.post("/fan-vote/verify-email/send"); toast.success("Check your inbox for the verification link"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not send email"); }
  };
  if (d.round.phase !== "open") return <p className="text-sm text-gray-400" data-testid="fanvote-vote-closed">{d.round.phase === "upcoming" ? "Voting hasn't opened yet." : "Voting has closed for this round."}</p>;
  if (!d.me) return <Link to={`/login?next=${next}`}><Button className="bg-purple-600 hover:bg-purple-700" data-testid="fanvote-signin-to-vote">Sign in or create a free account to vote</Button></Link>;
  if (d.me.voted_entry_id) return <p className="text-sm text-emerald-300 font-bold" data-testid="fanvote-already-voted">{d.me.voted_entry_id === entryId ? "You voted for this song in this round." : "You've already used your vote in this round."}</p>;
  if (d.me.is_artist) return <p className="text-sm text-gray-400" data-testid="fanvote-own-entry">This is your entry. Share the link with your fans!</p>;
  if (!d.me.verified_via) return (
    <div className="space-y-2" data-testid="fanvote-verify-needed">
      <p className="text-sm text-gray-300">Verify your email to vote (Google accounts are already verified).</p>
      <Button onClick={sendVerify} variant="outline" data-testid="fanvote-send-verify-btn">Send verification email</Button>
    </div>
  );
  return (
    <div>
      <input type="text" name="website" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] w-px h-px opacity-0" />
      <Button onClick={vote} disabled={busy} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-black px-8 h-12" data-testid="fanvote-vote-btn"><FanChoiceIcon size="sm" className="mr-2" testId="fanvote-vote-btn-icon" />Vote for this song</Button>
      <p className="text-[11px] text-gray-500 mt-2">One free vote per account per round. It can't be changed.</p>
    </div>
  );
};

export default function FanVoteEntryPage() {
  const { entryId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [me, setMe] = useState(null);
  const load = useCallback(() => api.get(`/fan-vote/entries/${entryId}`).then((r) => setD(r.data)).catch(() => setErr(true)), [entryId]);
  useEffect(() => { load(); api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, [load]);
  const follow = async () => {
    try { await api.post(`/artist/${d.artist.user_id}/follow`, { follower_id: me.user_id }); toast.success("Following"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not follow"); }
  };
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Voting link copied"); };
  if (err) return <div className="min-h-screen bg-[#08080c] text-gray-400 p-10" data-testid="fanvote-entry-missing">This entry isn't available. <Link to="/fan-vote" className="underline">See Fan Vote</Link></div>;
  if (!d) return <div className="min-h-screen bg-[#08080c] text-gray-500 p-10">Loading…</div>;
  const e = d.entry;
  const pageTitle = `Vote for ${e.track_title} by ${e.artist_name} | RGM Fan Vote`;
  return (
    <div className="min-h-screen bg-[#08080c]">
      <Helmet title={pageTitle} />
      <SharedNavigation currentUser={me} />
      <div className="max-w-2xl mx-auto pt-24 pb-20 px-4 sm:px-6 space-y-4" data-testid="fanvote-entry-page">
        <Link to="/fan-vote" className="text-xs text-purple-300 hover:underline" data-testid="fanvote-back-link">← {d.round.title}</Link>
        <Card className="bg-black/50 border border-white/10 p-5 space-y-4">
          <div className="flex gap-4 items-center">
            <img src={e.cover_url} alt="" className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl object-cover bg-gray-800" />
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black text-white" data-testid="fanvote-entry-title">{e.track_title}</h1>
              <Link to={`/artist/${d.artist?.user_id}`} className="text-purple-200 hover:underline" data-testid="fanvote-entry-artist">{e.artist_name}</Link>
              <p className="text-sm text-white font-bold mt-2" data-testid="fanvote-entry-votes">#{e.rank} · {e.votes} {e.votes === 1 ? "vote" : "votes"}{e.is_winner ? " · RGM Fan Choice winner" : ""}</p>
            </div>
          </div>
          <audio controls preload="none" src={e.audio_url} className="w-full" data-testid="fanvote-audio" />
          <Countdown round={d.round} />
          <VoteBox d={d} entryId={entryId} reload={load} />
          <div className="flex gap-2 flex-wrap pt-1">
            {d.me && !d.me.is_artist && (d.me.follows_artist
              ? <span className="text-xs text-emerald-300" data-testid="fanvote-following">Following {e.artist_name}</span>
              : <Button size="sm" variant="outline" onClick={follow} data-testid="fanvote-follow-btn"><Heart className="w-4 h-4 mr-1" />Follow artist</Button>)}
            <Button size="sm" variant="outline" onClick={share} data-testid="fanvote-share-btn"><Copy className="w-4 h-4 mr-1" />Copy voting link</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
